import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, FileCheck2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PageHeader, StatusBadge } from "./ui";

export default function Compliance() {
  const [audit, setAudit] = useState([]);
  const [gxp, setGxp] = useState([]);
  const [chain, setChain] = useState(null);

  useEffect(() => {
    api.get("/audit-trail").then(({ data }) => setAudit(data));
    api.get("/gxp-records").then(({ data }) => setGxp(data));
  }, []);

  const verify = async () => {
    try {
      const { data } = await api.get("/audit-trail/verify");
      setChain(data);
      if (data.valid) toast.success(`Chain verified · ${data.total} immutable entries`);
      else toast.error(`Integrity break at entry #${data.broken_at}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader eyebrow="COMPLIANCE VAULT" title="21 CFR Part 11" subtitle="Tamper-evident audit trail & GxP data integrity">
        <Button data-testid="verify-chain-btn" onClick={verify} className="rounded-none bg-secondary hover:bg-[hsl(var(--chart-2))] hover:text-black text-xs">
          <ShieldCheck className="h-4 w-4 mr-1" /> Verify Chain Integrity
        </Button>
      </PageHeader>

      <div className="p-8">
        {chain && (
          <div className={`mb-6 border p-4 flex items-center gap-3 ${chain.valid ? "border-[hsl(var(--chart-2))]/40 bg-[hsl(var(--chart-2))]/10" : "border-primary/40 bg-primary/10"}`} data-testid="chain-result">
            {chain.valid ? <ShieldCheck className="h-5 w-5 text-[hsl(var(--chart-2))]" /> : <ShieldAlert className="h-5 w-5 text-primary" />}
            <span className="font-mono text-xs tracking-widest">
              {chain.valid ? `AUDIT CHAIN INTACT · ${chain.total} ENTRIES · NO TAMPERING DETECTED` : `INTEGRITY BREACH AT #${chain.broken_at}`}
            </span>
          </div>
        )}

        <Tabs defaultValue="audit">
          <TabsList className="rounded-none bg-secondary/40 border border-border">
            <TabsTrigger value="audit" data-testid="tab-audit" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-white">Audit Trail</TabsTrigger>
            <TabsTrigger value="gxp" data-testid="tab-gxp" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-white">GxP Records</TabsTrigger>
          </TabsList>

          <TabsContent value="audit" className="mt-6">
            <div className="card-elevated rounded-sm overflow-hidden">
              <table className="w-full text-sm" data-testid="audit-table">
                <thead>
                  <tr className="border-b border-border text-left font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    <th className="px-4 py-3">#</th>
                    <th className="px-4 py-3">Action</th>
                    <th className="px-4 py-3">Target</th>
                    <th className="px-4 py-3">Actor</th>
                    <th className="px-4 py-3">Timestamp</th>
                    <th className="px-4 py-3">Hash</th>
                  </tr>
                </thead>
                <tbody>
                  {audit.map((a) => (
                    <tr key={a.id} className="border-b border-border last:border-0 hover:bg-white/[0.02] transition-colors">
                      <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{a.seq}</td>
                      <td className="px-4 py-3 font-mono text-xs text-[hsl(var(--chart-1))]">{a.action}</td>
                      <td className="px-4 py-3 text-muted-foreground">{a.target}</td>
                      <td className="px-4 py-3 font-mono text-xs">{a.actor}</td>
                      <td className="px-4 py-3 font-mono text-[10px] text-muted-foreground">{new Date(a.timestamp).toLocaleString()}</td>
                      <td className="px-4 py-3 font-mono text-[10px] text-muted-foreground">{a.entry_hash.slice(0, 12)}…</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="gxp" className="mt-6">
            <div className="grid md:grid-cols-2 gap-4" data-testid="gxp-list">
              {gxp.map((g) => (
                <div key={g.id} className="card-elevated rounded-sm p-5">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2"><FileCheck2 className="h-4 w-4 text-[hsl(var(--chart-2))]" /><span className="text-sm font-medium">{g.title}</span></div>
                    <StatusBadge value={g.integrity} />
                  </div>
                  <p className="font-mono text-[10px] text-muted-foreground mt-3 tracking-widest">{g.department.toUpperCase()} · SIGNED BY {g.signed_by}</p>
                  <p className="font-mono text-[10px] text-muted-foreground mt-1">sig:{g.signature.slice(0, 24)}…</p>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
